// Speech-to-Text Transcription Service
import { SERVER_URL } from '../config';

class TranscriptionService {
  constructor() {
    this.recognition = null;
    this.isListening = false;
    this.streamId = null;
    this.meetingId = null;
    this.username = null;
    this.onTranscriptUpdate = null;
    this.interimResults = [];
  }

  initialize(streamId, meetingId, username, onTranscriptUpdate) {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.error('Speech recognition not supported in this browser');
      return false;
    }

    this.streamId = streamId;
    this.meetingId = meetingId;
    this.username = username;
    this.onTranscriptUpdate = onTranscriptUpdate;

    // Initialize the SpeechRecognition API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
    
    // Configure recognition settings
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US'; // Default to English
    
    // Set up event handlers
    this.recognition.onresult = this.handleRecognitionResult.bind(this);
    this.recognition.onerror = this.handleRecognitionError.bind(this);
    this.recognition.onend = this.handleRecognitionEnd.bind(this);
    
    return true;
  }

  async startTranscription() {
    if (!this.recognition) return false;
    
    try {
      // Register transcription with the server
      const response = await fetch(`${SERVER_URL}/transcriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          streamId: this.streamId,
          meetingId: this.meetingId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to start transcription:', errorData);
        return false;
      }
      
      // Start the speech recognition
      this.recognition.start();
      this.isListening = true;
      console.log('Transcription started');
      return true;
    } catch (error) {
      console.error('Error starting transcription:', error);
      return false;
    }
  }

  stopTranscription() {
    if (!this.recognition || !this.isListening) return;
    
    this.recognition.stop();
    this.isListening = false;
    console.log('Transcription stopped');
    
    // Notify server that transcription has ended
    fetch(`${SERVER_URL}/transcriptions/${this.streamId}/end`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    }).catch(error => {
      console.error('Error ending transcription on server:', error);
    });
  }

  async handleRecognitionResult(event) {
    // Get the transcript from the results
    const results = event.results;
    const latestResult = results[results.length - 1];
    const transcript = latestResult[0].transcript;
    const confidence = latestResult[0].confidence;
    const isFinal = latestResult.isFinal;
    
    // Update interim results for UI display
    if (!isFinal) {
      this.interimResults = [{
        text: transcript,
        confidence: confidence,
        timestamp: new Date()
      }];
      
      // Call the update callback with interim results
      if (this.onTranscriptUpdate) {
        this.onTranscriptUpdate(this.interimResults, false);
      }
      return;
    }
    
    // Clear interim results when we have a final result
    this.interimResults = [];
    
    // Send the final transcript to the server
    try {
      const response = await fetch(`${SERVER_URL}/transcriptions/segment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          streamId: this.streamId,
          speakerUsername: this.username,
          text: transcript,
          confidence: confidence
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to save transcription segment:', errorData);
      } else {
        // Call the update callback with the final result
        if (this.onTranscriptUpdate) {
          const data = await response.json();
          this.onTranscriptUpdate([data.segment], true);
        }
      }
    } catch (error) {
      console.error('Error saving transcription segment:', error);
    }
  }

  handleRecognitionError(event) {
    console.error('Speech recognition error:', event.error);
    
    // Restart recognition if it was aborted or network error
    if (this.isListening && (event.error === 'aborted' || event.error === 'network')) {
      setTimeout(() => {
        if (this.isListening) {
          this.recognition.start();
          console.log('Restarting speech recognition after error');
        }
      }, 1000);
    }
  }

  handleRecognitionEnd() {
    console.log('Speech recognition ended');
    
    // Restart if we're still supposed to be listening
    if (this.isListening) {
      this.recognition.start();
      console.log('Restarting speech recognition');
    }
  }

  toggleLiveTranscription(enabled) {
    // Update server with live transcription preference
    fetch(`${SERVER_URL}/transcriptions/${this.streamId}/toggle`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ enabled })
    }).catch(error => {
      console.error('Error toggling live transcription:', error);
    });
  }
}

export default TranscriptionService;